import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import SectionHeading from "@/components/base/SectionHeading";
import StructuredData from "@/components/feature/StructuredData";
import PageMeta from "@/components/feature/PageMeta";
import FaqAccordion from "@/components/feature/FaqAccordion";
import PageNotFound from "@/pages/NotFound";
import { getAreaDetail, getAreaFaqs } from "@/mocks/areas";
import { contactInfo } from "@/mocks/site";
import { areaPageSlug } from "@/utils/areaSlug";
import { buildBreadcrumbSchema, absoluteUrl } from "@/utils/schemaBuilders";
import { HERO_IMAGES, AREA_HERO_IMAGES, AREA_NEARBY_IMAGES } from "@/config/heroImages";

const SITE = "https://laharipackersandmovers.in";

const trustPoints = [
  {
    icon: "ri-timer-flash-line",
    title: "Same-day local moves",
    text: "Most within-Bangalore shifts are packed, moved and unpacked in a single day.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Trained, uniformed crew",
    text: "Verified staff, own packing material and dedicated closed-body vehicles for every job.",
  },
  {
    icon: "ri-hand-coin-line",
    title: "Transparent estimates",
    text: "An item-wise quote before the move, with no surprise add-ons on shifting day.",
  },
];

export default function AreaDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const area = getAreaDetail(slug ?? "");
  const faqs = useMemo(() => getAreaFaqs(slug ?? ""), [slug]);

  const breadcrumbSchema = useMemo(() => {
    if (!area) return null;
    return buildBreadcrumbSchema([
      { label: "Home", href: "/" },
      { label: "Areas We Serve", href: "/areas-we-serve" },
      { label: area.name },
    ]);
  }, [area]);

  const serviceSchema = useMemo(() => {
    if (!area) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Local Shifting Service",
      name: `Local Packers and Movers in ${area.name}, Bangalore`,
      description: area.summary,
      url: `${SITE}/areas-we-serve/${area.slug}`,
      provider: {
        "@type": "MovingCompany",
        name: contactInfo.brand,
        image: absoluteUrl(contactInfo.logo),
        telephone: contactInfo.phone,
      },
      areaServed: { "@type": "Place", name: `${area.name}, Bangalore` },
      review: {
        "@type": "Review",
        author: { "@type": "Person", name: area.review.name },
        reviewRating: { "@type": "Rating", ratingValue: area.review.rating, bestRating: "5" },
        reviewBody: area.review.text,
      },
    };
  }, [area]);

  const faqSchema = useMemo(() => {
    if (faqs.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    };
  }, [faqs]);

  if (!area) return <PageNotFound />;

  return (
    <>
      <PageMeta
        title={`Packers and Movers in ${area.name}, Bangalore | Lahari`}
        description={`Local packers and movers in ${area.name}, Bangalore for same-day house, flat and office shifting. Get a free moving quote from Lahari.`}
        path={`/areas-we-serve/${area.slug}`}
      />
      {breadcrumbSchema && <StructuredData id="ld-area-breadcrumb" data={breadcrumbSchema} />}
      {serviceSchema && <StructuredData id="ld-area-service" data={serviceSchema} />}
      {faqSchema && <StructuredData id="ld-area-faq" data={faqSchema} />}

      <PageHero
        eyebrow={`${area.region} Bangalore`}
        title={`Packers and Movers in ${area.name}, Bangalore`}
        subtitle={area.tagline}
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Areas We Serve", href: "/areas-we-serve" },
          { label: area.name },
        ]}
        image={AREA_HERO_IMAGES[area.slug] ?? HERO_IMAGES.areaDetail}
      />

      {/* Intro + trust */}
      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto grid max-w-screen-2xl grid-cols-1 items-start gap-10 px-4 md:px-6 lg:grid-cols-[1.3fr_1fr] lg:gap-14">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Local Shifting"
              title={`Moving within ${area.name}`}
            />
            <div className="mt-5 space-y-4">
              {area.intro.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-relaxed text-foreground-600 md:text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <h3 className="mt-8 font-heading text-base font-semibold text-foreground-950">
              Landmarks we move around in {area.name}
            </h3>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {area.landmarks.map((landmark) => (
                <span
                  key={landmark}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-100 px-3.5 py-1.5 text-xs font-medium text-foreground-700"
                >
                  <i className="ri-map-pin-2-line text-accent-600" aria-hidden="true" />
                  {landmark}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={contactInfo.phoneHref}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-phone-fill text-lg" aria-hidden="true" /> Call {contactInfo.phone}
              </a>
              <Link
                to="/request-a-quote"
                data-cta="Get a Free Quote"
                data-cta-location="area-hero"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-6 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-file-text-line text-lg" aria-hidden="true" /> Get a Free Quote
              </Link>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                Why {area.name} families pick us
              </h3>
              <ul className="mt-5 space-y-4">
                {trustPoints.map((point) => (
                  <li key={point.title} className="flex gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className={`${point.icon} text-lg`} aria-hidden="true" />
                    </span>
                    <div>
                      <p className="font-heading text-sm font-semibold text-foreground-950">{point.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{point.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
              <div className="flex items-center gap-1 text-accent-600">
                {Array.from({ length: area.review.rating }).map((_, index) => (
                  <i key={index} className="ri-star-fill text-lg" aria-hidden="true" />
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">&ldquo;{area.review.text}&rdquo;</p>
              <p className="mt-4 text-sm font-semibold text-foreground-950">{area.review.name}</p>
              <p className="text-xs text-foreground-500">Customer in {area.name}, Bangalore</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="bg-background-50 py-14 md:py-16">
          <div className="mx-auto grid max-w-screen-2xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-[1.2fr_1fr] lg:gap-14">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                Frequently asked questions about moving in {area.name}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                What families moving within {area.name} ask us most.
              </p>
              <div className="mt-6">
                <FaqAccordion items={faqs} />
              </div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-route-line text-xl" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                Explore our local shifting service
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                Every move inside {area.name} is handled end to end by our local shifting crews — packing, loading,
                moving and setup.
              </p>
              <Link
                to="/services/local-shifting"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-arrow-right-line text-lg" aria-hidden="true" /> See Local Shifting
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Nearby areas */}
      {area.nearby.length > 0 && (
        <section className="bg-background-100 py-14 md:py-16">
          <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
            <SectionHeading
              eyebrow="Nearby Areas"
              title={`We also cover these localities near ${area.name}`}
              subtitle="Moving a little further? Our crews serve these neighbouring areas too."
            />
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {area.nearby.map((name) => (
                <Link
                  key={name}
                  to={`/areas-we-serve/${areaPageSlug(name)}`}
                  className="group flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                >
                  <span className="flex items-center gap-3">
                    <span className="h-12 w-12 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={AREA_NEARBY_IMAGES[areaPageSlug(name)]}
                        alt={`Packers and Movers in ${name}, Bangalore`}
                        title={`Packers and Movers in ${name}, Bangalore - Lahari`}
                        className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="font-heading text-base font-semibold text-foreground-950">
                      Packers and Movers in {name}
                    </span>
                  </span>
                  <i
                    className="ri-arrow-right-line text-lg text-primary-600 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </Link>
              ))}
            </div>
            <Link
              to="/areas-we-serve"
              className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-5 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
            >
              <i className="ri-map-2-line text-lg" aria-hidden="true" /> See all areas we serve
            </Link>
          </div>
        </section>
      )}

      <CtaBanner />
    </>
  );
}